import { EditIcon, ResetIcon, SquareCheckIcon, SquareTimesIcon } from '../../../../../assets/icons';
import { InterviewQuestionForEdit, PostJobStep } from '../../../../../store/workspace/types';
import MotionWrapper from '../../../../../layouts/MotionWrapper';
import { Dispatch, SetStateAction, useRef, useState } from 'react';
import TextArea from '../../../../../components/TextArea';
import CheckBox from '../../../../../components/CheckBox';
import Toggle from '../../../../../components/Toggle';
import Button from '../../../../../components/Button';
import styles from './style.module.scss';
import { useDispatch, useSelector } from '../../../../../store/store';
import { selectResetInterviewFormModal } from '../../../../../store/workspace/selectors';
import AddNewInterviewQuestion from '../../components/AddNewInterviewQuestion';
import { setAddNewInterviewQuestionModal, setResetInterviewFormModal } from '../../../../../store/workspace/slice';
import ConfirmResetInterviewForm from '../../components/ConfirmResetInterviewForm';
import { editExamThunk } from '../../../../../store/workspace/thunks';
import RecruitingInterviewDefaults from '../../components/RecruitingInterviewDefaults';
import AddRecruitingInterviewNewItem from '../../../../../containers/AddRecruitingInterviewNewItem';

interface Props {
    jobId: string | null;
    resetToDefault: () => void;
    recruitingInterview: boolean;
    isLoading: boolean;
    submitFunction: () => void;
    setStep: (step: PostJobStep) => void;
    defaultQuestions: InterviewQuestionForEdit[];
    selectedQuestions: InterviewQuestionForEdit[];
    setRecruitingInterview: Dispatch<SetStateAction<boolean>>;
    setDefaultQuestions: Dispatch<SetStateAction<InterviewQuestionForEdit[]>>;
    setSelectedQuestions: Dispatch<SetStateAction<InterviewQuestionForEdit[]>>;
};

export default function RecruitingInterview(props: Props) {
    const dispatch = useDispatch();
    const [textAreaVal, setTextAreaVal] = useState('');
    const [selectedQuestion, setSelectedQuestion] = useState(-1);
    const selectedListRef = useRef<HTMLDivElement | null>(null);
    const preDefinedListRef = useRef<HTMLDivElement | null>(null);
    // const addNewQuestionModal = useSelector(selectAddNewInterviewQuestionModal);
    const resetInterviewFormModal = useSelector(selectResetInterviewFormModal);
    const [showDefaultsModal, setShowDefaultsModal] = useState(false);
    const [showMobileAddNewQuestionModal, setShowMobileAddNewQuestionModal] = useState(false);
    const [addNewQuestionModal, setAddNewQuestionModal] = useState(false);

    const {
        jobId,
        isLoading,
        setStep,
        resetToDefault,
        submitFunction,
        defaultQuestions,
        selectedQuestions,
        setDefaultQuestions,
        recruitingInterview,
        setSelectedQuestions,
        setRecruitingInterview,
    } = props;

    const setEditMode = (indx: number, item: InterviewQuestionForEdit) => {
        setTextAreaVal(item.question);
        setSelectedQuestion(indx);
    };

    const editQuestion = (id: number) => {
        setSelectedQuestions(prev =>
            prev.map(question =>
                question.id === id ? { ...question, question: textAreaVal } : question
            ));
        setSelectedQuestion(-1);
        setTextAreaVal('');
    };

    const addNewQuestion = (question: string) => {
        const id = Math.max(
            ...defaultQuestions.map(i => i.id),
            ...selectedQuestions.map(i => i.id));

        setSelectedQuestions(prev => [...prev, {
            id: id + 1,
            question: question.trim(),
            domain: '',
            answerTime: '',
        }]);

        setAddNewQuestionModal(false);
    };

    const sendQuestionToSelectedList = (item: InterviewQuestionForEdit) => {
        if (selectedQuestions.length > 11) return;
        setSelectedQuestions(prev => [...prev, item]);
        setDefaultQuestions(prev => prev.filter(i => i.id !== item.id));
        selectedListRef.current?.scrollTo({ top: selectedListRef.current.scrollHeight, behavior: 'smooth' });
    };

    const deSelectQuestionFromSelectedList = (item: InterviewQuestionForEdit) => {
        setDefaultQuestions(prev => [item, ...prev]);
        setSelectedQuestions(prev => prev.filter(i => i.question !== item.question));
        preDefinedListRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleAddNewQuestion = () => {
        if (window.innerWidth < 960) {
            setShowMobileAddNewQuestionModal(true);
        } else {
            setAddNewQuestionModal(true);
        }
    };

    return (
        <MotionWrapper className={styles.container}>
            <span className={styles.title}>Step 2 - Recruiting Interview</span>
            {/* <div className={styles.activate}>
                <span className={styles.activate_title}>
                    Generate recruiting interview
                </span>
                <Toggle
                    toggled={recruitingInterview}
                    setToggle={() => setRecruitingInterview(prev => !prev)}
                />
            </div> */}
            {recruitingInterview && (<div className={styles.main}>
                <div className={styles.main_pre_defined} ref={preDefinedListRef}>
                    <span className={styles.main_pre_defined_title}>
                        List of suggested questions to ask:
                    </span>
                    <div className={styles.main_pre_defined_list}>
                        {defaultQuestions.map(item => <CheckBox
                            text={item.question}
                            checked={false}
                            onClick={() => sendQuestionToSelectedList(item)}
                        />)}
                    </div>
                </div>
                <div className={styles.main_selected}>
                    <div className={styles.main_selected_head}>
                        <span className={styles.main_selected_head_title}>
                            List of questions you would like to be asked:
                        </span>
                        <div className={styles.main_selected_head_count}>
                            <span>{selectedQuestions.length}/12</span>
                        </div>
                    </div>
                    <div
                        className={styles.main_selected_reset}
                        onClick={() => dispatch(setResetInterviewFormModal(true))}
                    >
                        <ResetIcon />
                        <span>Reset to default list</span>
                    </div>
                    <div className={styles.main_selected_list} ref={selectedListRef}>
                        {selectedQuestions.map((item, indx) =>
                            <div className={styles.main_selected_list_item}>
                                {selectedQuestion !== indx &&
                                    <div className={styles.main_selected_list_item_row}>
                                        <CheckBox
                                            checked
                                            text={`${indx + 1}- ${item.question}`}
                                            onClick={() => deSelectQuestionFromSelectedList(item)}
                                        />
                                        {/* <span>{`${indx + 1}- ${item.title}`}</span> */}
                                        <EditIcon
                                            width={25}
                                            height={25}
                                            onClick={() => setEditMode(indx, item)}
                                        />
                                    </div>}
                                {selectedQuestion === indx &&
                                    <div className={styles.main_selected_list_item_edit}>
                                        <CheckBox text='' checked />
                                        <TextArea
                                            value={textAreaVal}
                                            onChange={setTextAreaVal}
                                            className={styles.main_selected_list_item_edit_input}
                                        />
                                        <div className={styles.main_selected_list_item_edit_actions}>
                                            <SquareCheckIcon width={26} height={26} onClick={() => editQuestion(item.id)} />
                                            <SquareTimesIcon onClick={() => setSelectedQuestion(-1)} />
                                        </div>
                                    </div>}
                            </div>)}
                        <Button
                            type='Outline-Primary'
                            text='+ Add new question'
                            disabled={selectedQuestions.length > 11}
                            onClick={handleAddNewQuestion}
                        />
                    </div>
                </div>
            </div>)}
            <div className={styles.actions}>
                <Button
                    text='Back'
                    type='Outline-Primary'
                    className={styles.actions_btn}
                    onClick={() => setStep('JobDetail')}
                />
                <Button
                    type='Primary'
                    text='Save & Continue'
                    className={styles.actions_btn}
                    onClick={submitFunction}
                    loading={isLoading}
                />
            </div>

            {addNewQuestionModal && <AddNewInterviewQuestion addFunc={addNewQuestion} closeFunc={() => setAddNewQuestionModal(false)} />}
            {resetInterviewFormModal && <ConfirmResetInterviewForm resetFunc={resetToDefault} />}

            {showDefaultsModal && <RecruitingInterviewDefaults
                questions={defaultQuestions}
                selectedQuestionsLength={selectedQuestions.length}
                closeFunc={() => setShowDefaultsModal(false)}
                setSelectedQuestions={setSelectedQuestions}
                setDefaultQuestions={setDefaultQuestions}
            />}

            {showMobileAddNewQuestionModal &&
                <AddRecruitingInterviewNewItem
                    closeFunc={() => setShowMobileAddNewQuestionModal(false)}
                    addFromList={() => setShowDefaultsModal(true)}
                    addNewQuestion={() => setAddNewQuestionModal(true)}
                />}
        </MotionWrapper>
    )
}
